/*************************************************************************
* ADOBE CONFIDENTIAL
* ___________________
*
* Copyright 2019 Adobe
* All Rights Reserved.
*
* NOTICE: All information contained herein is, and remains
* the property of Adobe and its suppliers, if any. The intellectual
* and technical concepts contained herein are proprietary to Adobe
* and its suppliers and are protected by all applicable intellectual
* property laws, including trade secret and copyright laws.
* Dissemination of this information or reproduction of this material
* is strictly forbidden unless prior written permission is obtained
* from Adobe.
**************************************************************************/

import React from "react";
import {
    BrowserRouter, MemoryRouter, Route,
} from "react-router-dom";
import { observer } from "mobx-react";
import PropTypes from "prop-types";
import HeaderContainer from "app/preview/react-component/header/container/HeaderContainer";
import FileViewContainer from "app/preview/react-component/preview/container/file-view/FileViewContainer";
import AppCss from "app/preview/react-component/main-app/presentation/MainApp.css";
import ServiceRegistry from "bundle/util/ServiceRegistry";
import { DialogContainer, Provider, defaultTheme } from "@adobe/react-spectrum";
import NormalLegalDialog from "../../header/presentation/legal/LegalDialogContainer/LegalDialogContainer";
import SizedPresetFullScreenDialog
from "../../header/presentation/legal/LegalDialogContainer/SizedContainerFullScreenLegalDialogContainer";

const Header = observer(() => {
    const appStore = ServiceRegistry.getService("AppStore");
    const config = appStore.getConfig();

    let showTopBar = config.showTopBar;
    if (appStore.enableModernViewer && appStore.isSizedPreset) {
        showTopBar = appStore.inFullScreen;
    }

    const hideDiv = {};
    if (!showTopBar) {
        hideDiv.display = "none";
    }

    return (
        <div style={ hideDiv }>
            <HeaderContainer />
            <NormalLegalDialog />
        </div>
    );
});

Header.propTypes = {
    store: PropTypes.objectOf(PropTypes.any).isRequired,
};

const LicenseErrorPopup = () => (
    <Provider theme={ defaultTheme } colorScheme="light">
        <DialogContainer onDismiss={ () => {} }>
            {
                ServiceRegistry.getService("AppStore").licenseErrorPopupOpen
                && (
                    ServiceRegistry.getService("DialogApi").showLicenseErrorPopup()
                )
            }
        </DialogContainer>
    </Provider>
);

const Content = observer(props => {
    const appStore = ServiceRegistry.getService("AppStore");
    return !appStore.licenseErrorPopupDismissed && appStore.licenseErrorPopupOpen ? LicenseErrorPopup() : (
        <React.Fragment>
            {appStore.licenseErrorPopupDismissed ? "" : (
                <Route
                    render={ routeprops => <Header { ...props } { ...routeprops } /> }
                />
            )}
            <div className={ AppCss.middle }>
                {/* eslint-disable max-len */}
                {appStore.licenseErrorPopupDismissed ? appStore.intl.formatMessage({ id: "dialog.license.error.message" }) : (
                    <Route
                        render={ routeprops => <FileViewContainer { ...props } { ...routeprops } /> }
                    />
                )}
            </div>
            {/* The following div will be removed when AJS provides Legal dialog fix */}
            <div
                style={
                    {
                        position: "absolute",
                        zIndex: 10000,
                        top: 0,
                    }
                }
            >
                {
                    appStore.isSizedPreset && appStore.inFullScreen && appStore.legalDialogOpen
                    && <SizedPresetFullScreenDialog />
                }
            </div>
        </React.Fragment>
    );
});

/**
 * Top-level layout for app
 */
const App = props => {
    const content = () => (
        <div className={ AppCss.app }>
            <Content { ...props } />
        </div>
    );

    if (props.useBrowserRouter) {
        return (
            <BrowserRouter>
                {content()}
            </BrowserRouter>
        );
    }
    return (
        <MemoryRouter>
            {content()}
        </MemoryRouter>
    );
};

App.propTypes = {
    useBrowserRouter: PropTypes.bool.isRequired,
};

export default App;
